import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../utils/axiosInstance';
import { apiPost, apiPut, apiDelete } from '../utils/api';
import { socket } from '../socket';
import UniboxSidebar from '../components/unibox/UniboxSidebar';
import ChatWindow from '../components/inbox/ChatWindow';
import UniboxDetail from '../components/unibox/UniboxDetail';
import AddLeadModal from '../components/crm/AddLeadModal';
import ImportModal from '../components/crm/ImportModal';
import { Card, CardBody, TableWrapper, Table, Th, Td } from '../components/ui';
import { useModal } from '../context/ModalContext';
import { FaPlus, FaFileImport, FaTrash, FaComments, FaColumns, FaThList, FaPencilAlt, FaTimes } from 'react-icons/fa';
import { summarizeLeadInbox } from '../utils/uniboxComposer';

function normalizePhone(str) {
  if (!str) return '';
  return String(str).replace(/@s\.whatsapp\.net|@g\.us|@lid/g, '').replace(/\D/g, '');
}

function formatPhone(phone) {
  const clean = normalizePhone(phone);
  if (!clean) return '';
  if (clean.length >= 10) {
    const cc = clean.slice(0, clean.length - 10);
    const num = clean.slice(-10);
    return cc ? `+${cc} ${num.slice(0, 5)} ${num.slice(5)}` : num;
  }
  return clean;
}

function jidToPhone(jid) {
  if (!jid) return '';
  return jid.replace(/@s\.whatsapp\.net|@g\.us|@lid/g, '');
}

export default function Unibox() {
  const modal = useModal();

  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState('');
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [nameMap, setNameMap] = useState({});
  const [nameMapKey, setNameMapKey] = useState(0);
  const [mergedContacts, setMergedContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [chat, setChat] = useState([]);
  const [chatCursor, setChatCursor] = useState(null);
  const [chatHasMore, setChatHasMore] = useState(false);
  const [chatLoadingMore, setChatLoadingMore] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [contactFilter, setContactFilter] = useState('all');
  const [stageFilter, setStageFilter] = useState('');
  const [view, setView] = useState('chat');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [flows, setFlows] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [pipelineStages, setPipelineStages] = useState([]);
  const [editingStage, setEditingStage] = useState(null); // { key, label, color } or null
  const [addingStage, setAddingStage] = useState(false);
  const [newStageLabel, setNewStageLabel] = useState('');
  const [newStageColor, setNewStageColor] = useState('#6B7280');

  // Combined init: fetch everything Unibox needs in ONE API call
  const initUnibox = useCallback(async (sid) => {
    if (!sid) return;
    try {
      const res = await api.get(`/api/unibox/init?sessionId=${sid}`);
      const d = res.data;
      if (!d.success) return;

      // Sessions
      const connected = (d.sessions || []).filter(s => s.status === 'connected');
      setSessions(connected);

      // Pipeline
      if (Array.isArray(d.pipeline)) setPipelineStages(d.pipeline);

      // Flows
      if (Array.isArray(d.flows)) setFlows(d.flows);

      // Conversations (sidebar)
      setClients(d.conversations || []);

      // Contacts
      setContacts(d.contacts || []);

      // Name map (contacts + groups)
      const map = {};
      for (const c of (d.contacts || [])) {
        const cName = [c.firstName, c.lastName].filter(Boolean).join(' ') || '';
        if (c.phone && cName) {
          const cleanPhone = c.phone.replace(/\D/g, '');
          map[cleanPhone + '@s.whatsapp.net'] = cName;
          map[c.phone + '@s.whatsapp.net'] = cName;
        }
      }
      for (const g of (d.groups || [])) {
        if (g.id && g.name) map[g.id] = g.name;
      }
      setNameMap(map);
    } catch (err) {
      console.error('Unibox init failed:', err);
    }
  }, []);

  // Fetch connected sessions on mount, then init
  useEffect(() => {
    api.get('/api/sessions').then(res => {
      const all = Array.isArray(res.data) ? res.data : res.data.data || [];
      const connected = all.filter(s => s.status === 'connected');
      setSessions(connected);
      if (connected.length > 0) {
        setSessionId(connected[0].sessionId);
        initUnibox(connected[0].sessionId);
      }
    });
  }, [initUnibox]);

  // Re-init when session changes
  useEffect(() => {
    if (sessionId) initUnibox(sessionId);
  }, [sessionId, nameMapKey, initUnibox]);

  // Save pipeline stages helper
  const savePipelineStages = async (stages) => {
    setPipelineStages(stages);
    await apiPut('/api/settings/pipeline', { stages });
  };

  const handleAddStage = () => {
    if (!newStageLabel.trim()) return;
    const key = newStageLabel.trim().toLowerCase().replace(/\s+/g, '_');
    if (pipelineStages.some(s => s.key === key)) {
      modal.alert('A stage with this key already exists.');
      return;
    }
    const updated = [...pipelineStages, { key, label: newStageLabel.trim(), color: newStageColor }];
    savePipelineStages(updated);
    setNewStageLabel('');
    setNewStageColor('#6B7280');
    setAddingStage(false);
  };

  const handleEditStage = (stageKey, newLabel, newColor) => {
    const updated = pipelineStages.map(s => s.key === stageKey ? { ...s, label: newLabel, color: newColor } : s);
    savePipelineStages(updated);
    setEditingStage(null);
  };

  const handleDeleteStage = async (stageKey) => {
    if (stageKey === 'new' || stageKey === 'lost') {
      modal.alert("'New' and 'Lost' stages cannot be deleted.");
      return;
    }
    if (await modal.confirm(`Delete stage "${pipelineStages.find(s => s.key === stageKey)?.label}"? Contacts in this stage will remain but won't appear in any column.`, { danger: true })) {
      const updated = pipelineStages.filter(s => s.key !== stageKey);
      savePipelineStages(updated);
    }
  };

  // Helper to resolve display name for a JID
  const getDisplayName = useCallback((jid) => {
    if (!jid) return '';
    if (nameMap[jid]) return nameMap[jid];
    const isGroup = jid.endsWith('@g.us');
    const clean = jidToPhone(jid);
    if (isGroup) return 'Group Chat';
    if (clean.length >= 10) {
      const cc = clean.slice(0, clean.length - 10);
      const num = clean.slice(-10);
      return `+${cc} ${num.slice(0, 5)} ${num.slice(5)}`;
    }
    return `+${clean}`;
  }, [nameMap]);

  // Old separate fetches replaced by initUnibox (single combined API call)

  // Refresh helpers: re-run initUnibox (single call refreshes everything)
  const fetchClients = useCallback(async () => { if (sessionId) initUnibox(sessionId); }, [sessionId, initUnibox]);
  const fetchContacts = useCallback(async () => { if (sessionId) initUnibox(sessionId); }, [sessionId, initUnibox]);

  // Fetch clients + contacts when sessionId changes
  useEffect(() => {
    fetchClients();
    fetchContacts();
  }, [fetchClients, fetchContacts]);

  // Refetch all data
  const refetchAll = useCallback(() => {
    fetchClients();
    fetchContacts();
    setNameMapKey((k) => k + 1); // Rebuild nameMap with updated contact names
  }, [fetchClients, fetchContacts]);

  // Merge clients + contacts (unified API)
  useEffect(() => {
    const map = {};

    // Add chat clients
    for (const c of clients) {
      const phone = normalizePhone(c._id);
      if (!phone || c._id?.endsWith('@g.us')) {
        // Groups: add as chat-only, skip contact matching
        map[c._id] = { phone: c._id, jid: c._id, name: '', lastMessage: c.lastMessage, lastTime: c.lastTime, unreadCount: c.unreadCount, isChat: true, isLead: false, isGroup: true };
        continue;
      }
      map[phone] = { phone, jid: c._id, name: '', lastMessage: c.lastMessage, lastTime: c.lastTime, unreadCount: c.unreadCount, isChat: true, isLead: false };
    }

    // Merge unified contacts — contacts from API already have isLead, firstName, lastName, etc.
    for (const contact of contacts) {
      const phone = normalizePhone(contact.phone);
      if (!phone) continue;
      const contactName = [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.name || '';
      if (map[phone]) {
        map[phone].isLead = !!contact.isLead;
        map[phone].id = contact.id;
        map[phone]._id = contact.id;
        map[phone].stage = contact.stage;
        map[phone].leadScore = contact.leadScore || 0;
        map[phone].tags = contact.tags;
        map[phone].source = contact.source;
        map[phone].email = contact.email;
        map[phone].firstName = contact.firstName;
        map[phone].lastName = contact.lastName;
        map[phone].pushName = contact.pushName;
        map[phone].bio = contact.bio;
        map[phone].avatarUrl = contact.avatarUrl;
        map[phone].company = contact.company;
        map[phone].sessionId = contact.sessionId;
        // Contact name always overrides (if set)
        if (contactName) map[phone].name = contactName;
        if (!map[phone].jid) map[phone].jid = phone + '@s.whatsapp.net';
      } else {
        // No conversation row for this contact → no chat activity. Leave lastTime null
        // so it sorts below any active chat (which always has a real lastMessageTime).
        map[phone] = { phone, jid: phone + '@s.whatsapp.net', name: contactName, email: contact.email, firstName: contact.firstName, lastName: contact.lastName, pushName: contact.pushName, bio: contact.bio, avatarUrl: contact.avatarUrl, company: contact.company, sessionId: contact.sessionId, lastMessage: '', lastTime: null, unreadCount: 0, isChat: false, isLead: !!contact.isLead, id: contact.id, _id: contact.id, stage: contact.stage, leadScore: contact.leadScore || 0, tags: contact.tags, source: contact.source };
      }
    }

    // Sort by recency (WhatsApp Web behaviour). Unread count is a visual marker, not a
    // sort key — otherwise an old chat with 50 unread floats above a brand-new message.
    const sorted = Object.values(map).sort((a, b) => {
      const aT = a.lastTime ? new Date(a.lastTime).getTime() : 0;
      const bT = b.lastTime ? new Date(b.lastTime).getTime() : 0;
      return bT - aT;
    });
    setMergedContacts(sorted);
  }, [clients, contacts]);

  // Filter contacts
  useEffect(() => {
    let result = mergedContacts;
    if (contactFilter === 'leads') result = result.filter(c => c.isLead);
    if (contactFilter === 'other') result = result.filter(c => !c.isLead);
    if (stageFilter) result = result.filter(c => c.isLead && c.stage === stageFilter);
    if (searchText) {
      const q = searchText.toLowerCase();
      result = result.filter(c => {
        const name = getDisplayName(c.jid).toLowerCase();
        return name.includes(q) || (c.phone || '').includes(q) || (c.email || '').toLowerCase().includes(q);
      });
    }
    setFilteredContacts(result);
  }, [mergedContacts, contactFilter, stageFilter, searchText, getDisplayName]);

  const inboxSummary = useMemo(() => summarizeLeadInbox(mergedContacts), [mergedContacts]);

  // Load chat history for selected contact + mark as read
  const loadChat = useCallback(async () => {
    if (!selectedContact?.jid || !sessionId) return;
    try {
      const res = await api.get(`/api/whatsapp/${sessionId}/history/${encodeURIComponent(selectedContact.jid)}?limit=50`);
      const messages = res.data?.data || [];
      setChat(messages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)));
      setChatHasMore(res.data?.hasMore || false);
      setChatCursor(res.data?.nextCursor || null);

      // Mark conversation as read (fire-and-forget)
      api.post('/api/conversations/mark-read', {
        sessionId,
        contactJid: selectedContact.jid,
      }).catch(() => {});
    } catch (err) {
      console.error('Failed to load chat:', err);
      setChat([]);
      setChatHasMore(false);
      setChatCursor(null);
    }
  }, [selectedContact, sessionId]);

  // Load older messages (scroll up pagination)
  const loadMoreChat = useCallback(async () => {
    if (!selectedContact?.jid || !sessionId || !chatCursor || chatLoadingMore) return;
    setChatLoadingMore(true);
    try {
      const res = await api.get(`/api/whatsapp/${sessionId}/history/${encodeURIComponent(selectedContact.jid)}?limit=50&before=${encodeURIComponent(chatCursor)}`);
      const older = res.data?.data || [];
      setChat(prev => [...older.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)), ...prev]);
      setChatHasMore(res.data?.hasMore || false);
      setChatCursor(res.data?.nextCursor || null);
    } catch (err) {
      console.error('Failed to load more chat:', err);
    } finally {
      setChatLoadingMore(false);
    }
  }, [selectedContact, sessionId, chatCursor, chatLoadingMore]);

  useEffect(() => {
    loadChat();
  }, [loadChat]);

  // Join/leave socket chat rooms when selected contact changes
  useEffect(() => {
    if (!sessionId || !selectedContact?.jid) return;
    socket.emit('joinChat', { sessionId, recipient: selectedContact.jid });
    return () => {
      socket.emit('leaveChat', { sessionId, recipient: selectedContact.jid });
    };
  }, [sessionId, selectedContact?.jid]);

  // Listen for real-time messages via socket
  useEffect(() => {
    if (!sessionId) return;

    const handler = (msg) => {
      if (!msg) return;

      const chatId = msg.chatId || msg.sender || msg.recipient;
      const isInbound = msg.direction !== 'out' && msg.sender !== sessionId;
      const isOpenChat = selectedContact?.jid && (chatId === selectedContact.jid || msg.sender === selectedContact.jid || msg.recipient === selectedContact.jid);

      // Append to active chat if this message belongs to the open conversation
      if (isOpenChat) {
        setChat(prev => {
          // Deduplicate — the same message can arrive via both the per-chat room
          // and the user room, and again after our optimistic bubble is confirmed.
          const key = msg.messageId || msg.whatsappMessageId || msg._id;
          if (key && prev.some(m => (m.messageId || m.whatsappMessageId || m._id) === key)) return prev;
          return [...prev, msg];
        });
      }

      // Optimistically bump the sidebar row instead of refetching (which hits the
      // 2-min cache and stays stale). The fresh-conversation backstop runs below.
      const previewBase = msg.content || msg.text || msg.body || msg.caption || '';
      const preview = msg.direction === 'out' ? `You: ${previewBase}` : previewBase;
      const ts = msg.createdAt || msg.timestamp || new Date().toISOString();
      setClients(prev => {
        const next = [...prev];
        const i = next.findIndex(c => c._id === chatId);
        const incUnread = isInbound && !isOpenChat ? 1 : 0;
        if (i >= 0) {
          next[i] = {
            ...next[i],
            lastMessage: preview,
            lastTime: ts,
            unreadCount: isOpenChat ? 0 : (next[i].unreadCount || 0) + incUnread,
          };
        } else if (chatId) {
          next.push({
            _id: chatId,
            lastMessage: preview,
            lastTime: ts,
            unreadCount: incUnread,
            isGroup: chatId.endsWith('@g.us'),
          });
        }
        return next;
      });

      // Tab-title notification for inbound messages when chat isn't open.
      if (isInbound && !isOpenChat && typeof document !== 'undefined' && document.hidden) {
        const baseTitle = document.title.replace(/^\(\d+\)\s*/, '');
        const m = document.title.match(/^\((\d+)\)/);
        const n = (m ? parseInt(m[1], 10) : 0) + 1;
        document.title = `(${n}) ${baseTitle}`;
      }
    };

    // Reset tab title when window regains focus
    const onFocus = () => {
      if (typeof document === 'undefined') return;
      document.title = document.title.replace(/^\(\d+\)\s*/, '');
    };
    if (typeof window !== 'undefined') window.addEventListener('focus', onFocus);

    socket.on('newMessage', handler);
    return () => {
      socket.off('newMessage', handler);
      if (typeof window !== 'undefined') window.removeEventListener('focus', onFocus);
    };
  }, [selectedContact, sessionId]);

  // Send message
  const handleSend = async (messageText) => {
    if (!sessionId || !selectedContact?.jid) return;

    const number = normalizePhone(selectedContact.jid);

    const localId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const optimisticMsg = {
      _id: localId,
      messageId: localId,
      sender: sessionId,
      recipient: selectedContact.jid,
      content: messageText,
      direction: 'out',
      status: 'pending',
      timestamp: new Date().toISOString()
    };

    // Show the message immediately — don't wait for the API round-trip.
    setChat(prev => [...prev, optimisticMsg]);

    // Optimistically bump the sidebar row to the top with the outbound preview.
    setClients(prev => {
      const next = [...prev];
      const i = next.findIndex(c => c._id === selectedContact.jid);
      const preview = `You: ${messageText}`;
      if (i >= 0) {
        next[i] = { ...next[i], lastMessage: preview, lastTime: optimisticMsg.timestamp };
      } else {
        next.push({ _id: selectedContact.jid, lastMessage: preview, lastTime: optimisticMsg.timestamp, unreadCount: 0, isGroup: selectedContact.jid.endsWith('@g.us') });
      }
      return next;
    });

    try {
      const res = await api.post(`/api/whatsapp/${sessionId}/send`, {
        number,
        message: messageText,
      });

      const confirmedId = res?.data?.messageId;
      setChat(prev => prev.map(m => m.messageId === localId
        ? { ...m, messageId: confirmedId || m.messageId, status: 'sent', whatsappMessageId: confirmedId || undefined }
        : m));
    } catch (err) {
      console.error('Send failed:', err);
      setChat(prev => prev.map(m => m.messageId === localId ? { ...m, status: 'failed' } : m));
      const apiErr = err?.response?.data?.error || err?.response?.data?.message || err.message;
      modal.alert(apiErr || 'Failed to send message', 'error');
    }
  };

  const handleSelectContact = (contact) => {
    setSelectedContact(contact);
    setShowDetail(false);
    // Optimistically zero the unread badge so the sidebar reflects the read state
    // immediately, without waiting for the mark-read POST + refetch cycle.
    if (contact?.jid) {
      setClients(prev => prev.map(c => c._id === contact.jid ? { ...c, unreadCount: 0 } : c));
    }
  };

  const handleStageChange = async (contactId, newStage) => {
    await apiPut('/api/contacts/' + contactId, { stage: newStage });
    refetchAll();
  };

  const handleDeleteConversation = async () => {
    if (!sessionId || !selectedContact?.jid) return;
    const ok = await modal.confirm(
      'Delete this conversation? This clears the chat history and resets any AI/flow for this contact so you can start over. This cannot be undone.',
      { danger: true, confirmText: 'Delete' }
    );
    if (!ok) return;
    try {
      await apiDelete(`/api/conversations?sessionId=${encodeURIComponent(sessionId)}&contactJid=${encodeURIComponent(selectedContact.jid)}`);
      setSelectedContact(null);
      setChat([]);
      setShowDetail(false);
      refetchAll();
    } catch (err) {
      modal.alert('Failed to delete conversation.', 'error');
    }
  };

  // --- Render helpers ---

  const renderChatView = () => (
    <div className="flex h-full overflow-hidden">
      <UniboxSidebar
        sessions={sessions} sessionId={sessionId} setSessionId={setSessionId}
        contacts={filteredContacts} selectedContact={selectedContact}
        onSelect={handleSelectContact} searchText={searchText} setSearchText={setSearchText}
        contactFilter={contactFilter} setContactFilter={setContactFilter}
        stageFilter={stageFilter} setStageFilter={setStageFilter}
        getDisplayName={getDisplayName}
        pipelineStages={pipelineStages}
        className={selectedContact ? 'hidden md:flex' : 'flex'}
      />
      <div className={`flex-1 min-w-0 overflow-hidden ${selectedContact ? 'flex' : 'hidden md:flex'} flex-col`}>
        <ChatWindow
          selectedClient={selectedContact?.jid || null}
          chat={chat} setChat={setChat} sessionId={sessionId}
          onSend={handleSend}
          onRefresh={() => { loadChat(); fetchClients(); }}
          displayName={selectedContact ? (selectedContact.name || getDisplayName(selectedContact.jid) || formatPhone(selectedContact.phone) || formatPhone(selectedContact.jid) || 'Unknown') : ''}
          contact={selectedContact}
          pipelineStages={pipelineStages}
          onBack={() => setSelectedContact(null)}
          onToggleDetail={selectedContact ? () => setShowDetail(!showDetail) : undefined}
          onDeleteConversation={selectedContact ? handleDeleteConversation : undefined}
          leadStage={selectedContact?.isLead ? selectedContact.stage : undefined}
          onLoadMore={loadMoreChat}
          hasMore={chatHasMore}
          loadingMore={chatLoadingMore}
        />
      </div>
      {/* Desktop detail panel */}
      {showDetail && selectedContact && (
        <div className="hidden md:flex">
          <UniboxDetail contact={selectedContact} onClose={() => setShowDetail(false)} onUpdate={refetchAll} flows={flows} isMobile={false} />
        </div>
      )}
      {/* Mobile detail overlay */}
      {showDetail && selectedContact && (
        <div className="md:hidden">
          <UniboxDetail contact={selectedContact} onClose={() => setShowDetail(false)} onUpdate={refetchAll} flows={flows} isMobile={true} />
        </div>
      )}
    </div>
  );

  const renderKanban = () => {
    const leadContacts = mergedContacts.filter(c => c.isLead);
    return (
      <div className="flex gap-4 p-4 h-full overflow-x-auto">
        {pipelineStages.map(stage => {
          const stageLeads = leadContacts.filter(c => c.stage === stage.key);
          const isEditing = editingStage?.key === stage.key;
          return (
            <div key={stage.key} className="min-w-[220px] flex-shrink-0 flex flex-col bg-surface-bg rounded-xl overflow-hidden"
              onDragOver={e => e.preventDefault()}
              onDrop={e => { const contactId = e.dataTransfer.getData('contactId'); if (contactId) handleStageChange(contactId, stage.key); }}>
              <div className="px-3 py-2 text-white text-sm font-semibold flex justify-between items-center" style={{ backgroundColor: stage.color }}>
                {isEditing ? (
                  <div className="flex items-center gap-1 flex-1" onClick={e => e.stopPropagation()}>
                    <input type="text" defaultValue={editingStage.label} className="text-sm bg-white/20 text-white placeholder:text-white/60 rounded px-1 py-0.5 w-20 outline-none" id={`edit-label-${stage.key}`} />
                    <input type="color" defaultValue={editingStage.color} className="w-6 h-6 rounded cursor-pointer border-0" id={`edit-color-${stage.key}`} />
                    <button className="text-white/80 hover:text-white" onClick={() => {
                      const label = document.getElementById(`edit-label-${stage.key}`).value;
                      const color = document.getElementById(`edit-color-${stage.key}`).value;
                      handleEditStage(stage.key, label, color);
                    }} title="Save">&#10003;</button>
                    <button className="text-white/80 hover:text-white" onClick={() => setEditingStage(null)} title="Cancel"><FaTimes size={10} /></button>
                  </div>
                ) : (
                  <>
                    <span>{stage.label}</span>
                    <div className="flex items-center gap-1.5">
                      <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">{stageLeads.length}</span>
                      <button className="text-white/60 hover:text-white" onClick={() => setEditingStage({ key: stage.key, label: stage.label, color: stage.color })} title="Edit stage"><FaPencilAlt size={10} /></button>
                      {stage.key !== 'new' && stage.key !== 'lost' && (
                        <button className="text-white/60 hover:text-white" onClick={() => handleDeleteStage(stage.key)} title="Delete stage"><FaTimes size={10} /></button>
                      )}
                    </div>
                  </>
                )}
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-2">
                {stageLeads.map(c => (
                  <div key={c._id} draggable onDragStart={e => e.dataTransfer.setData('contactId', c._id)}
                    onClick={() => { setSelectedContact(c); setShowDetail(true); }}
                    className="bg-white rounded-lg p-3 shadow-sm cursor-pointer hover:shadow-md transition-shadow border border-surface-border">
                    <div className="text-sm font-medium text-ink-primary">{c.name || c.phone}</div>
                    <div className="text-xs text-ink-muted">{c.phone}</div>
                    {c.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {c.tags.slice(0, 2).map((t, i) => (
                          <span key={i} className="px-1.5 py-0.5 rounded text-[10px] bg-surface-bg text-ink-muted">{t}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Add new stage column */}
        <div className="min-w-[220px] flex-shrink-0 flex flex-col items-center justify-start bg-surface-bg rounded-xl border-2 border-dashed border-surface-border p-4">
          {addingStage ? (
            <div className="w-full space-y-2">
              <input type="text" placeholder="Stage name" value={newStageLabel} onChange={e => setNewStageLabel(e.target.value)}
                className="input text-sm w-full" autoFocus />
              <div className="flex items-center gap-2">
                <label className="text-xs text-ink-muted">Color:</label>
                <input type="color" value={newStageColor} onChange={e => setNewStageColor(e.target.value)} className="w-8 h-8 rounded cursor-pointer border-0" />
              </div>
              <div className="flex gap-2">
                <button className="btn-primary btn-sm flex-1" onClick={handleAddStage}>Add</button>
                <button className="btn-secondary btn-sm flex-1" onClick={() => { setAddingStage(false); setNewStageLabel(''); }}>Cancel</button>
              </div>
            </div>
          ) : (
            <button className="flex items-center gap-1.5 text-ink-muted hover:text-ink-primary transition-colors text-sm mt-2" onClick={() => setAddingStage(true)}>
              <FaPlus size={12} /> Add Stage
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderList = () => {
    // Require a real _id so checkbox state and DELETE /api/contacts/:id
    // work correctly. Without this, chat-only contacts (no _id) collapse
    // into a single `undefined` key in selectedLeads — selecting any one
    // row marks every undefined-id row as "selected" and the delete loop
    // fires `DELETE /api/contacts/undefined` for them all.
    const leadContacts = mergedContacts.filter(c => c.isLead && c._id);
    return (
      <div className="p-4 h-full overflow-y-auto">
        {selectedLeads.size > 0 && (
          <div className="flex items-center gap-3 mb-3 p-2 bg-brand-50 rounded-lg">
            <span className="text-sm text-ink-primary">{selectedLeads.size} selected</span>
            <select className="input text-sm w-auto" onChange={e => { if (e.target.value) { apiPost('/api/contacts/bulk-update-stage', { contactIds: [...selectedLeads], stage: e.target.value }); setSelectedLeads(new Set()); refetchAll(); } }} defaultValue="">
              <option value="" disabled>Change stage...</option>
              {pipelineStages.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <button className="btn-danger btn-sm" onClick={async () => { if (await modal.confirm('Delete selected leads?', { danger: true })) { let failed = 0; for (const id of selectedLeads) { if (!id) { failed++; continue; } try { await apiDelete('/api/contacts/' + id); } catch (e) { failed++; console.error('delete contact failed', id, e); } } setSelectedLeads(new Set()); refetchAll(); if (failed) modal.alert(`${failed} contact${failed === 1 ? '' : 's'} could not be deleted`, 'error'); } }}><FaTrash size={11} /></button>
          </div>
        )}
        <Card>
          <CardBody>
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th><input type="checkbox" onChange={e => { if (e.target.checked) setSelectedLeads(new Set(leadContacts.map(c => c._id))); else setSelectedLeads(new Set()); }} /></Th>
                    <Th>Name</Th><Th>Phone</Th><Th>Email</Th><Th>Stage</Th><Th>Source</Th><Th>Tags</Th>
                  </tr>
                </thead>
                <tbody>
                  {leadContacts.map(c => (
                    <tr key={c._id} className="cursor-pointer hover:bg-surface-bg" onClick={() => { setSelectedContact(c); setShowDetail(true); }}>
                      <Td onClick={e => e.stopPropagation()}><input type="checkbox" checked={selectedLeads.has(c._id)} onChange={e => { const next = new Set(selectedLeads); if (e.target.checked) next.add(c._id); else next.delete(c._id); setSelectedLeads(next); }} /></Td>
                      <Td>{c.name || '-'}</Td>
                      <Td className="font-mono text-xs">{c.phone}</Td>
                      <Td>{c.email || '-'}</Td>
                      <Td><span className="px-2 py-0.5 rounded-full text-[10px] font-medium text-white" style={{ backgroundColor: pipelineStages.find(s => s.key === c.stage)?.color || '#9CA3AF' }}>{pipelineStages.find(s => s.key === c.stage)?.label || c.stage}</span></Td>
                      <Td className="capitalize text-xs">{c.source || '-'}</Td>
                      <Td>{c.tags?.slice(0, 2).map((t, i) => <span key={i} className="px-1.5 py-0.5 rounded text-[10px] bg-surface-bg text-ink-muted mr-1">{t}</span>)}</Td>
                    </tr>
                  ))}
                  {leadContacts.length === 0 && <tr><Td colSpan="7" className="text-center text-ink-muted py-6">No leads yet</Td></tr>}
                </tbody>
              </Table>
            </TableWrapper>
          </CardBody>
        </Card>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full overflow-hidden -mx-3 -my-4 md:-m-6" style={{ height: 'calc(100vh - var(--header-height))' }}>
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-surface-border bg-surface-card flex-shrink-0 gap-3 flex-wrap">
        <div>
          <h1 className="text-lg font-bold text-ink-primary">Lead Inbox</h1>
          <p className="text-xs text-ink-muted">WhatsApp conversations with CRM stage, score, and next-action context.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {[
            { label: 'Threads', value: inboxSummary.total },
            { label: 'Leads', value: inboxSummary.leads },
            { label: 'Unread', value: inboxSummary.unread },
            { label: 'Hot', value: inboxSummary.hot },
          ].map((item) => (
            <div key={item.label} className="px-2.5 py-1.5 rounded-lg border border-surface-border bg-surface-bg min-w-[64px]">
              <div className="text-[10px] uppercase tracking-wide text-ink-faint">{item.label}</div>
              <div className="text-sm font-semibold text-ink-primary">{item.value}</div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {[
            { key: 'chat', icon: <FaComments size={14} />, title: 'Chat' },
            { key: 'kanban', icon: <FaColumns size={14} />, title: 'Kanban' },
            { key: 'list', icon: <FaThList size={14} />, title: 'List' },
          ].map(v => (
            <button key={v.key} onClick={() => setView(v.key)} title={v.title}
              className={`p-2 rounded-lg border transition-colors ${
                view === v.key ? 'bg-brand text-white border-brand' : 'border-surface-border text-ink-secondary hover:bg-surface-bg'
              }`}>
              {v.icon}
            </button>
          ))}
          <button className="btn-primary btn-sm flex items-center gap-1" onClick={() => setShowAddModal(true)}>
            <FaPlus size={10} /> Add Lead
          </button>
          <button className="btn-secondary btn-sm flex items-center gap-1" onClick={() => setShowImportModal(true)}>
            <FaFileImport size={10} /> Import
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-hidden">
        {view === 'chat' && renderChatView()}
        {view === 'kanban' && renderKanban()}
        {view === 'list' && renderList()}
      </div>

      {/* Detail panel for kanban/list views */}
      {showDetail && selectedContact && view !== 'chat' && (
        <>
          <div className="hidden md:block fixed right-0 top-0 h-full z-40">
            <UniboxDetail contact={selectedContact} onClose={() => setShowDetail(false)} onUpdate={refetchAll} flows={flows} isMobile={false} />
          </div>
          <div className="md:hidden">
            <UniboxDetail contact={selectedContact} onClose={() => setShowDetail(false)} onUpdate={refetchAll} flows={flows} isMobile={true} />
          </div>
        </>
      )}

      {/* Modals */}
      {showAddModal && <AddLeadModal onClose={() => setShowAddModal(false)} onCreated={refetchAll} sessionId={sessionId} />}
      {showImportModal && <ImportModal onClose={() => setShowImportModal(false)} onImported={refetchAll} />}
    </div>
  );
}
